import * as Sentry from '@sentry/react'
import { v7 as uuidv7 } from 'uuid'

import { apiService } from './apiService'

// The device has one active session. Serialize transitions across route visits
// so a delayed start/end from a previous screen cannot outlive a new start.
let pendingTransition = Promise.resolve()

function enqueue(
    operation: 'start' | 'end',
    sessionId: string,
    action: () => Promise<void>
): Promise<void> {
    pendingTransition = pendingTransition
        .then(action)
        .catch((error: unknown) => {
            Sentry.captureException(error, {
                tags: { feature: 'session-recording', operation },
                extra: { sessionId }
            })
            console.error(`Session recording ${operation} failed`, error)
        })
    return pendingTransition
}

export function beginSessionRecording() {
    const sessionId = uuidv7()
    let activeSessionId = sessionId
    let stopped = false
    let startRequested = false
    let stopPromise: Promise<void> | undefined

    void enqueue('start', sessionId, async () => {
        if (stopped) return
        startRequested = true
        const response = await apiService.startDeviceSession(sessionId)
        const existingId = response.data.sessionId
        if (existingId && existingId !== sessionId) {
            // A kiosk restart or failed stop can leave an older session active.
            // /start does not replace it, even when sent a new session ID.
            activeSessionId = existingId
            await apiService.endDeviceSession(existingId)
            startRequested = false
            if (stopped) return
            activeSessionId = sessionId
            startRequested = true
            const restarted = await apiService.startDeviceSession(sessionId)
            if (restarted.data.sessionId !== sessionId) {
                activeSessionId = restarted.data.sessionId ?? sessionId
                throw new Error('Device recording session was not replaced')
            }
        }
    })

    return {
        sessionId,
        stop(): Promise<void> {
            if (stopPromise) return stopPromise
            stopped = true
            stopPromise = enqueue('end', sessionId, async () => {
                // Also stop after an uncertain/failed start: the device may have
                // started recording even if its response never reached us.
                if (startRequested) {
                    await apiService.endDeviceSession(activeSessionId)
                }
            })
            return stopPromise
        }
    }
}
